import { useState, useRef, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Plus, Loader2, Camera, FileUp, X, Eye, EyeOff, Phone, Mail, CheckCheck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ALL_PERMISSIONS, ROLE_PRESETS } from "@/lib/permissions";

const CURRENCIES = [
  { value: "EGP", label: "جنيه مصري (EGP)" },
  { value: "SAR", label: "ريال سعودي (SAR)" },
  { value: "AED", label: "درهم إماراتي (AED)" },
  { value: "KWD", label: "دينار كويتي (KWD)" },
  { value: "QAR", label: "ريال قطري (QAR)" },
  { value: "BHD", label: "دينار بحريني (BHD)" },
  { value: "OMR", label: "ريال عماني (OMR)" },
  { value: "JOD", label: "دينار أردني (JOD)" },
  { value: "USD", label: "دولار أمريكي (USD)" },
];

function phoneToEmail(phone: string): string {
  const cleaned = phone.replace(/\D/g, "");
  return `${cleaned}@phone.nursery.local`;
}

const staffSchema = z.object({
  full_name: z.string().trim().min(2, "الاسم يجب أن يكون حرفين على الأقل").max(100),
  role: z.string().min(1, "يرجى اختيار الوظيفة"),
  system_role: z.string().default("teacher"),
  phone: z.string().trim().max(20).optional().or(z.literal("")),
  salary_type: z.enum(["monthly", "per_session", "percentage"]).default("monthly"),
  salary: z.string().optional().or(z.literal("")),
  session_price: z.string().optional().or(z.literal("")),
  commission_percentage: z.string().optional().or(z.literal("")),
  currency: z.string().default("EGP"),
  allowances: z.string().optional().or(z.literal("")),
  bonuses: z.string().optional().or(z.literal("")),
  deductions: z.string().optional().or(z.literal("")),
  create_account: z.boolean().default(false),
  login_method: z.enum(["email", "phone"]).default("phone"),
  email: z.string().email("بريد إلكتروني غير صالح").optional().or(z.literal("")),
  login_phone: z.string().trim().optional().or(z.literal("")),
  password: z.string().min(6, "كلمة المرور يجب أن تكون 6 أحرف على الأقل").optional().or(z.literal("")),
}).refine((data) => {
  if (data.create_account) {
    if (data.login_method === "email") {
      return !!data.email && !!data.password;
    } else {
      return !!data.login_phone && !!data.password;
    }
  }
  return true;
}, { message: "بيانات تسجيل الدخول وكلمة المرور مطلوبان عند إنشاء حساب", path: ["email"] });

type StaffFormValues = z.infer<typeof staffSchema>;

interface AddStaffDialogProps {
  onSuccess: () => void;
}

const AddStaffDialog = ({ onSuccess }: AddStaffDialogProps) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [docFiles, setDocFiles] = useState<File[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(ROLE_PRESETS.teacher);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const { profile } = useAuth();

  const form = useForm<StaffFormValues>({
    resolver: zodResolver(staffSchema),
    defaultValues: { full_name: "", role: "teacher", system_role: "teacher", phone: "", salary_type: "monthly", salary: "", session_price: "", commission_percentage: "", currency: "EGP", allowances: "", bonuses: "", deductions: "", create_account: false, login_method: "phone", email: "", login_phone: "", password: "" },
  });

  const createAccount = form.watch("create_account");
  const loginMethod = form.watch("login_method");
  const systemRole = form.watch("system_role");
  const salaryType = form.watch("salary_type");

  // When system_role changes, preset permissions
  useEffect(() => {
    const preset = ROLE_PRESETS[systemRole];
    if (preset) {
      setSelectedPermissions([...preset]);
    }
  }, [systemRole]);

  const togglePermission = (key: string) => {
    setSelectedPermissions(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const selectAll = () => setSelectedPermissions(ALL_PERMISSIONS.map(p => p.key));
  const deselectAll = () => setSelectedPermissions(["dashboard", "settings"]);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت"); return; }
      setPhotoFile(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleDocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) toast.error("تم تجاهل ملفات أكبر من 10 ميجابايت");
    setDocFiles(prev => [...prev, ...valid]);
  };

  const removeDoc = (index: number) => setDocFiles(prev => prev.filter((_, i) => i !== index));

  const onSubmit = async (values: StaffFormValues) => {
    if (!profile?.nursery_id) { toast.error("لم يتم العثور على حضانتك"); return; }
    setLoading(true);
    try {
      let photo_url: string | null = null;

      if (photoFile) {
        const ext = photoFile.name.split(".").pop();
        const path = `${profile.nursery_id}/${crypto.randomUUID()}.${ext}`;
        const { error: upErr } = await supabase.storage.from("staff-photos").upload(path, photoFile);
        if (upErr) throw new Error("خطأ في رفع الصورة: " + upErr.message);
        photo_url = path;
      }

      const { data: staffData, error: staffErr } = await supabase.from("staff").insert({
        nursery_id: profile.nursery_id,
        full_name: values.full_name,
        role: values.role,
        phone: values.phone || null,
        salary_type: values.salary_type,
        salary: values.salary ? parseFloat(values.salary) : null,
        session_price: values.session_price ? parseFloat(values.session_price) : 0,
        commission_percentage: values.commission_percentage ? parseFloat(values.commission_percentage) : 0,
        currency: values.currency,
        allowances: values.allowances ? parseFloat(values.allowances) : 0,
        bonuses: values.bonuses ? parseFloat(values.bonuses) : 0,
        deductions: values.deductions ? parseFloat(values.deductions) : 0,
        photo_url,
      } as any).select("id").single();

      if (staffErr) throw new Error("خطأ في إضافة الموظف: " + staffErr.message);

      // Save permissions
      if (staffData && selectedPermissions.length > 0) {
        const permRows = selectedPermissions.map(p => ({
          staff_id: staffData.id,
          nursery_id: profile.nursery_id,
          permission: p,
        }));
        await supabase.from("staff_permissions" as any).insert(permRows);
      }

      // Upload documents
      if (docFiles.length > 0 && staffData) {
        for (const doc of docFiles) {
          const ext = doc.name.split(".").pop();
          const path = `${profile.nursery_id}/${staffData.id}/${crypto.randomUUID()}.${ext}`;
          const { error: docUpErr } = await supabase.storage.from("staff-documents").upload(path, doc);
          if (docUpErr) { console.error("Doc upload error:", docUpErr); continue; }
          await supabase.from("staff_documents" as any).insert({
            staff_id: staffData.id,
            nursery_id: profile.nursery_id,
            file_name: doc.name,
            file_url: path,
            file_type: doc.type,
            file_size: doc.size,
          });
        }
      }

      // Create account if requested
      if (values.create_account && values.password && staffData) {
        const accountEmail = values.login_method === "phone"
          ? phoneToEmail(values.login_phone || "")
          : values.email || "";
        
        if (!accountEmail) {
          toast.error("بيانات تسجيل الدخول ناقصة");
        } else {
          const res = await supabase.functions.invoke("create-staff-account", {
            body: {
              email: accountEmail,
              password: values.password,
              full_name: values.full_name,
              phone: values.login_method === "phone" ? values.login_phone : (values.phone || null),
              staff_id: staffData.id,
              nursery_id: profile.nursery_id,
              role: values.system_role,
            },
          });
          if (res.error || res.data?.error) {
            toast.error("تم إضافة الموظف لكن فشل إنشاء الحساب: " + (res.data?.error || res.error?.message));
          } else {
            toast.success("تم إنشاء حساب الموظف بنجاح!");
          }
        }
      }

      toast.success("تم إضافة الموظف بنجاح!");
      form.reset();
      setPhotoFile(null);
      setPhotoPreview(null);
      setDocFiles([]);
      setSelectedPermissions(ROLE_PRESETS.teacher);
      setOpen(false);
      onSuccess();
    } catch (err: any) {
      toast.error(err.message || "حدث خطأ");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gradient-hero border-0">
          <Plus className="w-4 h-4 ml-2" />
          إضافة موظف
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle>إضافة موظف جديد</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Photo Upload */}
            <div className="flex flex-col items-center gap-2">
              <Avatar className="w-20 h-20 cursor-pointer border-2 border-dashed border-muted-foreground/30" onClick={() => photoInputRef.current?.click()}>
                <AvatarImage src={photoPreview || undefined} />
                <AvatarFallback><Camera className="w-6 h-6 text-muted-foreground" /></AvatarFallback>
              </Avatar>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
              <span className="text-xs text-muted-foreground">اضغط لرفع صورة</span>
            </div>

            <FormField control={form.control} name="full_name" render={({ field }) => (
              <FormItem>
                <FormLabel>الاسم الكامل *</FormLabel>
                <FormControl><Input placeholder="اسم الموظف" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="role" render={({ field }) => (
              <FormItem>
                <FormLabel>الوظيفة *</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="اختر الوظيفة" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="teacher">مدرس/مدرسة</SelectItem>
                    <SelectItem value="supervisor">مشرف/مشرفة</SelectItem>
                    <SelectItem value="accountant">محاسب/محاسبة</SelectItem>
                    <SelectItem value="admin">إداري/إدارية</SelectItem>
                    <SelectItem value="driver">سائق</SelectItem>
                    <SelectItem value="cleaner">عامل نظافة</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="phone" render={({ field }) => (
                <FormItem>
                  <FormLabel>رقم الهاتف</FormLabel>
                  <FormControl><Input placeholder="05xxxxxxxx" dir="ltr" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="currency" render={({ field }) => (
                <FormItem>
                  <FormLabel>العملة</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl><SelectTrigger><SelectValue placeholder="اختر العملة" /></SelectTrigger></FormControl>
                    <SelectContent>
                      {CURRENCIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            {/* Salary Type Selection */}
            <div className="border rounded-lg p-3 space-y-3">
              <label className="text-sm font-medium text-foreground">نوع الراتب</label>
              <FormField control={form.control} name="salary_type" render={({ field }) => (
                <FormItem>
                  <div className="flex gap-2">
                    {[
                      { value: "monthly", label: "شهري", icon: "💰" },
                      { value: "per_session", label: "بالحصة", icon: "🎓" },
                      { value: "percentage", label: "نسبة", icon: "📊" },
                    ].map(opt => (
                      <Button
                        key={opt.value}
                        type="button"
                        size="sm"
                        variant={field.value === opt.value ? "default" : "outline"}
                        className="flex-1 gap-1"
                        onClick={() => field.onChange(opt.value)}
                      >
                        {opt.icon} {opt.label}
                      </Button>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )} />

              {salaryType === "monthly" && (
                <FormField control={form.control} name="salary" render={({ field }) => (
                  <FormItem>
                    <FormLabel>الراتب الشهري</FormLabel>
                    <FormControl><Input type="number" placeholder="0.00" dir="ltr" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              )}

              {salaryType === "per_session" && (
                <FormField control={form.control} name="session_price" render={({ field }) => (
                  <FormItem>
                    <FormLabel>سعر الحصة الواحدة</FormLabel>
                    <FormControl><Input type="number" placeholder="0.00" dir="ltr" {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">سيتم حساب الراتب: عدد الحصص × سعر الحصة</p>
                    <FormMessage />
                  </FormItem>
                )} />
              )}

              {salaryType === "percentage" && (
                <FormField control={form.control} name="commission_percentage" render={({ field }) => (
                  <FormItem>
                    <FormLabel>النسبة المئوية %</FormLabel>
                    <FormControl><Input type="number" placeholder="0" dir="ltr" min="0" max="100" {...field} /></FormControl>
                    <p className="text-xs text-muted-foreground">سيتم حساب الراتب: إجمالي الإيرادات × النسبة</p>
                    <FormMessage />
                  </FormItem>
                )} />
              )}
            </div>

            <div className="border rounded-lg p-3 space-y-3">
              <label className="text-sm font-medium text-foreground">مكونات الراتب</label>
              <div className="grid grid-cols-3 gap-3">
                <FormField control={form.control} name="allowances" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">البدلات</FormLabel>
                    <FormControl><Input type="number" placeholder="0" dir="ltr" {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="bonuses" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">العلاوات</FormLabel>
                    <FormControl><Input type="number" placeholder="0" dir="ltr" {...field} /></FormControl>
                  </FormItem>
                )} />
                <FormField control={form.control} name="deductions" render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs">الخصومات</FormLabel>
                    <FormControl><Input type="number" placeholder="0" dir="ltr" {...field} /></FormControl>
                  </FormItem>
                )} />
              </div>
            </div>

            {/* Documents Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium">مستندات التعيين (اختياري)</label>
              <div className="border border-dashed border-muted-foreground/30 rounded-lg p-3">
                <Button type="button" variant="outline" size="sm" className="w-full" onClick={() => docInputRef.current?.click()}>
                  <FileUp className="w-4 h-4 ml-2" />
                  رفع مستندات
                </Button>
                <input ref={docInputRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" className="hidden" onChange={handleDocChange} />
                {docFiles.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {docFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between text-xs bg-muted rounded px-2 py-1">
                        <span className="truncate flex-1">{f.name}</span>
                        <button type="button" onClick={() => removeDoc(i)}><X className="w-3 h-3 text-muted-foreground hover:text-destructive" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ===== Permissions Section ===== */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <label className="text-sm font-bold text-foreground">🔐 صلاحيات الخدمات</label>
                <div className="flex gap-1">
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={selectAll}>
                    <CheckCheck className="w-3 h-3" /> تحديد الكل
                  </Button>
                  <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={deselectAll}>
                    <RotateCcw className="w-3 h-3" /> إعادة تعيين
                  </Button>
                </div>
              </div>

              {/* Quick presets */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { key: "admin", label: "إداري (كل شيء)" },
                  { key: "teacher", label: "مدرس" },
                  { key: "supervisor", label: "مشرف" },
                  { key: "accountant", label: "محاسب" },
                ].map(preset => (
                  <Button
                    key={preset.key}
                    type="button"
                    size="sm"
                    variant={JSON.stringify(selectedPermissions.sort()) === JSON.stringify([...ROLE_PRESETS[preset.key]].sort()) ? "default" : "outline"}
                    className="h-7 text-xs"
                    onClick={() => setSelectedPermissions([...ROLE_PRESETS[preset.key]])}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                {ALL_PERMISSIONS.map(perm => {
                  const Icon = perm.icon;
                  const checked = selectedPermissions.includes(perm.key);
                  const isRequired = perm.key === "dashboard" || perm.key === "settings";
                  return (
                    <label
                      key={perm.key}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                        checked ? "bg-primary/10 border-primary/30" : "bg-background border-border hover:bg-muted/50"
                      } ${isRequired ? "opacity-70" : ""}`}
                    >
                      <Checkbox
                        checked={checked}
                        disabled={isRequired}
                        onCheckedChange={() => !isRequired && togglePermission(perm.key)}
                      />
                      <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium truncate">{perm.label}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{perm.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground">✅ {selectedPermissions.length} من {ALL_PERMISSIONS.length} خدمة مفعّلة — الموظف سيرى فقط الأيقونات المحددة</p>
            </div>

            {/* Account Creation Toggle */}
            <div className="border rounded-lg p-3 space-y-3">
              <FormField control={form.control} name="create_account" render={({ field }) => (
                <FormItem className="flex items-center justify-between">
                  <FormLabel className="mb-0">إنشاء حساب دخول للموظف</FormLabel>
                  <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                </FormItem>
              )} />

              {createAccount && (
                <div className="space-y-3">
                  <FormField control={form.control} name="system_role" render={({ field }) => (
                    <FormItem>
                      <FormLabel>صلاحيات النظام *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl><SelectTrigger><SelectValue placeholder="اختر الصلاحية" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="teacher">مدرس - يرى فصله فقط</SelectItem>
                          <SelectItem value="supervisor">مشرف - يرى كل البيانات</SelectItem>
                          <SelectItem value="accountant">محاسب - المالية فقط</SelectItem>
                          <SelectItem value="admin">إداري - صلاحيات كاملة</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />

                  <FormField control={form.control} name="login_method" render={({ field }) => (
                    <FormItem>
                      <FormLabel>طريقة تسجيل الدخول *</FormLabel>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant={field.value === "phone" ? "default" : "outline"} className="flex-1 gap-2" onClick={() => field.onChange("phone")}>
                          <Phone className="w-4 h-4" /> رقم الهاتف
                        </Button>
                        <Button type="button" size="sm" variant={field.value === "email" ? "default" : "outline"} className="flex-1 gap-2" onClick={() => field.onChange("email")}>
                          <Mail className="w-4 h-4" /> البريد الإلكتروني
                        </Button>
                      </div>
                    </FormItem>
                  )} />

                  {loginMethod === "phone" ? (
                    <FormField control={form.control} name="login_phone" render={({ field }) => (
                      <FormItem>
                        <FormLabel>رقم الهاتف لتسجيل الدخول *</FormLabel>
                        <FormControl><Input placeholder="01xxxxxxxxx" dir="ltr" type="tel" {...field} /></FormControl>
                        <p className="text-xs text-muted-foreground">سيستخدم الموظف رقم الهاتف وكلمة المرور لتسجيل الدخول</p>
                        <FormMessage />
                      </FormItem>
                    )} />
                  ) : (
                    <FormField control={form.control} name="email" render={({ field }) => (
                      <FormItem>
                        <FormLabel>البريد الإلكتروني *</FormLabel>
                        <FormControl><Input type="email" placeholder="staff@example.com" dir="ltr" {...field} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  )}

                  <FormField control={form.control} name="password" render={({ field }) => (
                    <FormItem>
                      <FormLabel>كلمة المرور *</FormLabel>
                      <div className="relative">
                        <FormControl><Input type={showPassword ? "text" : "password"} placeholder="******" dir="ltr" {...field} /></FormControl>
                        <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShowPassword(!showPassword)}>
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 gradient-hero border-0" disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "إضافة"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddStaffDialog;
